import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Box,
  FormControlLabel,
  Checkbox,
  Link
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import therapistApi from '../../services/therapistApi';

const AudioExportDialog = ({ 
  open, 
  onClose, 
  conversationId,
  messageCount 
}) => {
  const [clientCode, setClientCode] = useState('');
  const [includeSystemMessages, setIncludeSystemMessages] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async () => {
    if (!clientCode.trim()) {
      setError('Please enter a client code for identification');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await therapistApi.requestAudioExport(conversationId, {
        clientCode: clientCode.trim(),
        includeSystemMessages
      });

      if (response.data.success) {
        setSuccess({
          jobId: response.data.jobId,
          message: response.data.message,
          estimatedTime: response.data.estimatedTime
        });
        
        // Close dialog after 3 seconds
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    } catch (error) {
      console.error('Error requesting audio export:', error);
      setError(
        error.response?.data?.message || 
        'Failed to request audio export. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const estimatedDuration = Math.ceil((messageCount * 3) / 60); // Rough estimate: 3 seconds per message

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <DownloadIcon />
          <Typography variant="h6">Export Audio</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {success.message}
            </Typography>
            <Typography variant="body2">
              Estimated time: {success.estimatedTime}
            </Typography>
            <Typography variant="caption" display="block" mt={1}>
              You can check the status in the{' '}
              <Link 
                href="/therapist/audio-exports" 
                target="_blank"
                rel="noopener"
              >
                Audio Export Queue
              </Link>
            </Typography>
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                This will generate a downloadable audio file of the entire conversation.
                The process runs in the background and you'll be notified when it's ready.
              </Typography>
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Conversation details:
              </Typography>
              <Typography variant="body2">
                • {messageCount} messages
              </Typography>
              <Typography variant="body2">
                • Estimated duration: ~{estimatedDuration} minutes
              </Typography>
              <Typography variant="body2">
                • Processing time: 15-20 minutes
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Client Identification Code"
              value={clientCode}
              onChange={(e) => setClientCode(e.target.value)}
              margin="normal"
              required
              helperText="Enter a code to identify this client (e.g., initials, case number). This will be used in the filename."
              error={!clientCode.trim() && error}
              disabled={isSubmitting}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={includeSystemMessages}
                  onChange={(e) => setIncludeSystemMessages(e.target.checked)}
                  disabled={isSubmitting}
                />
              }
              label="Include system messages in export"
              sx={{ mt: 2 }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Privacy Notice:</strong> Client names and personal information 
                will not be included in the exported file. Only the client code you 
                provide will be used for identification.
              </Typography>
            </Alert>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting || !clientCode.trim()}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <QueueMusicIcon />}
          >
            {isSubmitting ? 'Requesting...' : 'Request Export'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AudioExportDialog;